import { useState } from "react";
import { Pencil, Upload, Info } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "./ui/dialog";
import CategoryPicker from "./CategoryPicker";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";

/**
 * Owner-only edit dialog for PhotoDetail.
 *
 * Three sections:
 *   1. Details    — title + description
 *   2. Shot       — every EXIF field, free-text. Useful when a JPEG had its
 *                   EXIF stripped (WhatsApp / iOS export / Photoshop Save for Web).
 *   3. Replace    — re-upload the image. By default we keep the existing EXIF
 *                   (Option A — the photographer just re-exported their edit).
 *                   The `reread_exif` checkbox flips that for the rare cases
 *                   where the new file has more accurate metadata.
 *
 * Props:
 *   - photo:      the PhotoOut object from /api/photos/{id}
 *   - onUpdated:  callback(updatedPhoto) — parent merges this into state.
 */
const EXIF_FIELDS = [
  { key: "camera_make", label_key: "edit_camera_make" },
  { key: "camera_model", label_key: "edit_camera_model" },
  { key: "lens", label_key: "edit_lens" },
  { key: "iso", label_key: "edit_iso" },
  { key: "aperture", label_key: "edit_aperture" },
  { key: "shutter", label_key: "edit_shutter" },
  { key: "focal_length", label_key: "edit_focal_length" },
  { key: "date_taken", label_key: "edit_date_taken" },
];

export default function EditPhotoDialog({ photo, onUpdated }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("details");
  const [title, setTitle] = useState(photo.title || "");
  const [description, setDescription] = useState(photo.description || "");
  const [category, setCategory] = useState(photo.category || null);
  const initialExif = () =>
    EXIF_FIELDS.reduce((acc, f) => {
      acc[f.key] = (photo.exif && photo.exif[f.key]) || "";
      return acc;
    }, {});
  const [exif, setExif] = useState(initialExif);
  const [file, setFile] = useState(null);
  const [rereadExif, setRereadExif] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTitle(photo.title || "");
    setDescription(photo.description || "");
    setCategory(photo.category || null);
    setExif(initialExif());
    setFile(null);
    setRereadExif(false);
    setTab("details");
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const body = { title, description, exif, category: category || "" };
      const { data } = await api.patch(`/photos/${photo.id}`, body);
      onUpdated?.(data);
      toast.success(t("edit_saved"));
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleReplace = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("reread_exif", rereadExif ? "true" : "false");
      // Don't set Content-Type — axios auto-generates the multipart boundary.
      const { data } = await api.put(`/photos/${photo.id}/image`, fd);
      onUpdated?.(data);
      toast.success(t("edit_replaced"));
      setOpen(false);
      // Force the browser to bust its image cache for this photo.
      // The image URL stays the same; we add a timestamp param at the call site.
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleFixOrientation = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/photos/${photo.id}/refresh-thumbnail`);
      onUpdated?.(data);
      toast.success(t("edit_orientation_fixed"));
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const TABS = [
    { key: "details", label: t("edit_section_details") },
    { key: "exif", label: t("edit_section_exif") },
    { key: "replace", label: t("edit_section_replace") },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          className="w-full inline-flex items-center justify-between border border-zinc-800 text-zinc-200 px-5 py-4 text-xs uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 transition-colors"
          data-testid="edit-photo-button"
        >
          <span className="inline-flex items-center gap-3 font-mono text-[11px]">
            <Pencil size={12} />
            {t("edit_photo")}
          </span>
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl bg-zinc-950 border border-zinc-800 text-zinc-100 p-0"
        data-testid="edit-photo-dialog"
      >
        <div className="px-6 pt-6 pb-2 border-b border-zinc-900">
          <DialogTitle asChild>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
              {t("edit_photo_title")}
            </div>
          </DialogTitle>
        </div>

        <div className="flex border-b border-zinc-900">
          {TABS.map((s) => (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              className={`flex-1 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors ${
                tab === s.key
                  ? "text-zinc-100 border-b-2 border-sienna"
                  : "text-zinc-500 border-b-2 border-transparent hover:text-zinc-300"
              }`}
              data-testid={`edit-tab-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
          {tab === "details" && (
            <div className="space-y-5" data-testid="edit-pane-details">
              <FieldLabel>{t("photo_title")}</FieldLabel>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-serif text-xl focus:outline-none focus:border-zinc-500"
                data-testid="edit-title-input"
              />
              <FieldLabel>{t("photo_description")}</FieldLabel>
              <textarea
                value={description}
                rows={5}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-200 font-light leading-relaxed focus:outline-none focus:border-zinc-500 resize-none"
                data-testid="edit-description-input"
              />
              <FieldLabel>{t("category")}</FieldLabel>
              <CategoryPicker value={category} onChange={setCategory} idPrefix="edit-cat" />
            </div>
          )}

          {tab === "exif" && (
            <div className="space-y-5" data-testid="edit-pane-exif">
              <div className="flex items-start gap-2 text-zinc-500 font-light text-sm leading-relaxed">
                <Info size={14} className="mt-0.5 shrink-0" />
                <p>{t("edit_exif_lead")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EXIF_FIELDS.map((f) => (
                  <div key={f.key}>
                    <FieldLabel>{t(f.label_key)}</FieldLabel>
                    <input
                      value={exif[f.key]}
                      onChange={(e) => setExif((x) => ({ ...x, [f.key]: e.target.value }))}
                      className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-zinc-100 font-mono text-sm focus:outline-none focus:border-zinc-500"
                      data-testid={`edit-exif-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "replace" && (
            <div className="space-y-5" data-testid="edit-pane-replace">
              <div className="pb-5 border-b border-zinc-900">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300">
                      {t("edit_fix_orientation")}
                    </div>
                    <p className="mt-2 text-zinc-500 font-light text-xs max-w-md">
                      {t("edit_fix_orientation_hint")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleFixOrientation}
                    disabled={busy}
                    className="shrink-0 inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:bg-zinc-900 hover:border-zinc-700 disabled:opacity-50 transition-colors"
                    data-testid="edit-fix-orientation-button"
                  >
                    {busy ? t("edit_fixing_orientation") : t("edit_fix_orientation")}
                  </button>
                </div>
              </div>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">
                {t("edit_replace_lead")}
              </p>
              <label className="block">
                <FieldLabel>{t("edit_replace_file")}</FieldLabel>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-zinc-300 file:mr-4 file:py-2 file:px-4 file:border file:border-zinc-800 file:bg-transparent file:text-zinc-200 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.3em] hover:file:bg-zinc-900"
                  data-testid="edit-replace-file-input"
                />
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rereadExif}
                  onChange={(e) => setRereadExif(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-sienna"
                  data-testid="edit-replace-reread-checkbox"
                />
                <div>
                  <div className="text-zinc-100 font-light text-sm">{t("edit_replace_reread")}</div>
                  <div className="text-zinc-500 font-light text-xs mt-1">
                    {t("edit_replace_reread_hint")}
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-zinc-900 flex justify-end gap-3">
          {tab === "replace" ? (
            <button
              onClick={handleReplace}
              disabled={busy || !file}
              className="inline-flex items-center gap-2 bg-sienna text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.3em] hover:bg-sienna-400 disabled:opacity-50 transition-colors"
              data-testid="edit-replace-submit"
            >
              <Upload size={12} />
              {busy ? t("edit_replacing") : t("edit_replace_submit")}
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={busy}
              className="bg-sienna text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.3em] hover:bg-sienna-400 disabled:opacity-50 transition-colors"
              data-testid="edit-save-button"
            >
              {busy ? t("edit_saving") : t("edit_save")}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
      {children}
    </div>
  );
}
